import { Link } from "react-router";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { Kicker } from "./Kicker";
import { fadeIn } from "./fadeIn";
import { DOCUMENTED_INTEGRATION_COUNT } from "@/data/integrationCount";
import { HUNT_HUB_URL, HUNT_MD_SPEC_URL } from "@/lib/links";

/**
 * Proof that is true today (story messaging §5). No customer names or logos,
 * and no user counts on customer-facing pages (Tyler, 2026-10-23).
 */
/** Published playbook count on hub.huntbase.io. Update when the Hub grows. */
const HUB_PLAYBOOKS = "240+";

const FACTS = [
  { figure: String(DOCUMENTED_INTEGRATION_COUNT), text: "documented integrations" },
  { figure: HUB_PLAYBOOKS, text: "open hunt playbooks on the Huntbase Hub" },
  { figure: "1,250+", text: "public attack recordings that watchers are tested against" },
  { figure: "2-person", text: "approval for actions on a host, with a full audit log" },
];

export function ProofRow() {
  const reduce = useReducedMotion();
  return (
    <section className="bg-card px-6 py-10 md:px-12 md:py-24" aria-labelledby="proof-heading">
      <div className="mx-auto flex max-w-7xl flex-col gap-8 md:gap-10">
        <motion.div {...fadeIn(reduce)} className="flex flex-col gap-4">
          <Kicker>True today</Kicker>
          <h2 id="proof-heading" className="heading-md text-ink text-balance">
            Open, in use, and built by people who have worked the breach.
          </h2>
        </motion.div>

        <ul className="grid grid-cols-2 gap-x-6 gap-y-6 md:grid-cols-4">
          {FACTS.map((f, i) => (
            <motion.li
              key={f.text}
              {...fadeIn(reduce, 0.05 * i)}
              className="flex flex-col gap-1 border-t-2 border-primary-500 pt-4"
            >
              <span className="text-3xl font-semibold tracking-tight text-ink md:text-4xl">
                {f.figure}
              </span>
              <span className="text-sm leading-snug text-ink-muted md:text-[15px]">{f.text}</span>
            </motion.li>
          ))}
        </ul>

        <motion.div
          {...fadeIn(reduce, 0.1)}
          className="grid gap-4 text-[15px] leading-relaxed text-ink-muted md:grid-cols-2 md:gap-10"
        >
          <p className="hidden md:block">
            Your hunts stay portable: the{" "}
            <a
              href={HUNT_MD_SPEC_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-ink underline decoration-rule underline-offset-4 hover:text-primary-600"
            >
              hunt.md format is an open spec
              <ArrowUpRight size={12} weight="bold" className="ml-0.5 inline" aria-hidden />
              <span className="sr-only">(opens in a new tab)</span>
            </a>
            , and Huntbase speaks Sigma, OCSF, STIX/TAXII and CACAO.{" "}
            <a
              href={HUNT_HUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-ink underline decoration-rule underline-offset-4 hover:text-primary-600"
            >
              Browse the Hub
              <ArrowUpRight size={12} weight="bold" className="ml-0.5 inline" aria-hidden />
              <span className="sr-only">(opens in a new tab)</span>
            </a>
            .
          </p>
          <p>
            Scout doesn&apos;t train shared models on your data.{" "}
            <span className="hidden md:inline">
              Built by a former Mandiant incident responder, Tyler Oliver (Founder and CEO), with
              Rakesh Mukherjee (CTO, previously EclecticIQ and McAfee).
            </span>{" "}
            <Link
              to="/company"
              className="group inline-flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-500"
            >
              Meet the team
              <ArrowRight
                size={14}
                weight="bold"
                aria-hidden
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
